import { useState, useEffect, useMemo } from "react";
import usePageTitle from "@/hooks/usePageTitle";
import Table, { TableHeading, TableControls, TableFooter, buildBulkSelectionColumn } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { DuesRatesRow, Variables, ContextType } from "./types";
import { DuesRates, PreviousPricing } from "@/types";
import { useQuery } from "@apollo/client";
import { GET_DUES_RATES_QUERY } from "./queries";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { toFormat } from "@/lib/dateHelpers";
import { sortBy, orderBy as orderByLodash } from "lodash";
import { ColumnSort } from "@tanstack/react-table";
import { Updater, SortingState } from "@tanstack/table-core";
import { Controller } from "react-hook-form";
import SearchInput from "@/components/SearchInput";
import { useSearch } from "@/hooks/useSearch";
import getActiveFilters from "@/lib/filterHelpers";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { AdjustRateModal } from "@/pages/billing/dues-management/manage-products/annual-national-rates/_components/AdjustRateModal";
import { FinalizePricingModal } from "@/pages/billing/dues-management/manage-products/annual-national-rates/_components/FinalizePricingModal";
import { Cell } from "@tanstack/react-table";
import { EditRateModal } from "@/pages/billing/dues-management/manage-products/annual-national-rates/_components/EditRateModal";
import { getColumns } from "./_components/TableColumns";
import {
  UPDATE_DUES_RATES_MUTATION,
  DUES_FINALIZE_RATES_MUTATION
} from "@/pages/billing/dues-management/manage-products/annual-national-rates/queries";
import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import useAmplitude from "@/hooks/useAmplitude";
import { useOutletContext, useParams } from "react-router-dom";
import { SECTION_ANALYTICS_EVENTS, getSectionAnalyticsData } from "@/pages/sections/_utils/amplitude";
import { DOMAIN, PERMISSION, useRequireCapability, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { GET_MANAGE_DUES_RATES_QUERY } from "@/pages/billing/dues-management/manage-products/annual-national-rates/queries";
import { get } from "lodash";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import {
  filterErrorsByKeys,
  getStatusById
} from "@/pages/billing/dues-management/manage-products/annual-national-rates/_utils";

type DuesRatesData = {
  duesRates: {
    nodes: DuesRates[];
  };
  nationalDuesRates: {
    nodes: DuesRates[];
  };
};

const getPriceByYear = (previousPricing: PreviousPricing[], year: string) => {
  return centsToDollarsFormatted(previousPricing?.filter((p) => p.year === year)?.[0]?.priceCents);
};

const formatTableData = ({
  data,
  errors = [],
  warnings = [],
  currentYear
}: {
  data: DuesRates[];
  errors: Record<string, string[]>[];
  warnings: Record<string, string[]>[];
  currentYear: number;
}) => {
  return data.map(
    ({ id, classification, classificationDescription, updatedAt, priceCents, previousPricing, finalized, notes }) => ({
      id,
      classification,
      classificationDescription,
      rate_current_year: centsToDollarsFormatted(priceCents),
      rate_previous_year: getPriceByYear(previousPricing, `${currentYear - 1}`),
      rate_previous_year_2: getPriceByYear(previousPricing, `${currentYear - 2}`),
      notes: notes.join("; ") || null,
      updatedAt: toFormat(updatedAt),
      classificationNumber: Number(classification?.split("-")?.[1]),
      classificationLetter: classification?.split("-")?.[0],
      status: getStatusById(errors, warnings, id).status,
      statusMessage: getStatusById(errors, warnings, id).statusMessage,
      finalized
    })
  );
};

export default function SectionalDuesRatesPage() {
  usePageTitle("/sections/:sectionId/billing/dues-rates", "Sections | Billing | Dues Rates");
  useRequireCapability({ domain: DOMAIN.SECTIONS, permission: PERMISSION.viewDuesRate });

  const { legacyId, setErrorMessage } = useOutletContext<ContextType>();
  const { sectionId } = useParams();
  const { trackEvent } = useAmplitude();
  const defaultSort = [{ id: "classification", desc: false }];
  const [orderBy, setOrderBy] = useState<ColumnSort[]>(defaultSort);
  const { searchValue, control } = useSearch("name");
  const defaultVariables = { kind: `section-${legacyId}` };
  const { isCompactLayout: isMobile } = useIsCompactLayout();

  const { data: manageDuesRatesData } = useQuery(GET_MANAGE_DUES_RATES_QUERY, {
    variables: {
      filters: {
        ...defaultVariables
      }
    }
  });
  const canManageDuesRates = useHasCapabilityBool({
    domain: DOMAIN.SECTIONS,
    permission: PERMISSION.manageDuesRates
  });

  const { canEdit, canFinalize } = get(manageDuesRatesData, "manageDuesRates", {});

  const [variables, setVariables] = useState<Variables>(defaultVariables);
  const [selectedClassificationValues, setSelectedClassificationValues] = useState<string[]>([]);
  const {
    rowSelection,
    setRowSelection,
    bulkSelectionProps,
    isSelectedOnly,
    deselectAll,
    rowSelectionCount: rowSelected
  } = useBulkSelection();
  const [openEditRateModal, setOpenEditRateModal] = useState(false);
  const [openAdjustRateModal, setOpenAdjustRateModal] = useState(false);
  const [openFinalizePricingModal, setOpenFinalizePricingModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>[]>([]);
  const currentYear = new Date().getFullYear();

  const { data, loading } = useQuery<DuesRatesData>(GET_DUES_RATES_QUERY, {
    variables: {
      filters: {
        ...variables
      }
    },
    skip: !legacyId,
    fetchPolicy: "cache-first"
  });

  const tableData: DuesRatesRow[] = sortBy(
    orderByLodash(
      formatTableData({ data: data?.duesRates?.nodes || [], errors, warnings: [], currentYear }),
      "classificationNumber"
    ),
    (item) => [item.classificationLetter]
  );

  const [updateDuesRates] = useMutation(UPDATE_DUES_RATES_MUTATION);
  const [finalizeDuesRates, { loading: finalizeLoading }] = useMutation(DUES_FINALIZE_RATES_MUTATION);

  const handleSort = (updaterOrValue: Updater<SortingState>) => {
    const sortState = typeof updaterOrValue === "function" ? updaterOrValue([]) : updaterOrValue;
    const { id, desc } = sortState[0] || {};
    setOrderBy([{ id, desc }]);
  };

  const filters = {
    status: selectedClassificationValues.length > 0
  };
  const { isActive } = getActiveFilters(filters, filters, true);

  useEffect(() => {
    setVariables((prev) => ({
      ...(searchValue ? { ...prev, search: searchValue } : { ...defaultVariables })
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    if (rowSelected === 0 && isSelectedOnly) {
      deselectAll();
    }
  }, [rowSelected, isSelectedOnly, deselectAll]);
  // TODO: filter by unfinalized status
  const filteredData = tableData.filter((item) => rowSelection[item.id]);

  const handleBulkEditRate = async (value: string) => {
    const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    updateDuesRates({
      variables: {
        input: {
          duesRateIds: selectedIds,
          priceCents: value
        }
      },
      refetchQueries: [
        {
          query: GET_DUES_RATES_QUERY,
          variables: {
            filters: {
              ...variables
            }
          }
        }
      ]
    }).then((response) => {
      if (response.data?.duesBulkEditRates?.success) {
        setOpenEditRateModal(false);
        showSnackbar("Rate(s) have been updated");
        trackEvent(
          SECTION_ANALYTICS_EVENTS.duesRatesUpdated,
          getSectionAnalyticsData({
            source: "section_dues_rates",
            sectionId,
            rateCount: selectedIds.length,
            action: "bulk_edit",
            membershipYear: currentYear
          })
        );
        if (isMobile && rowSelected === 1) {
          setRowSelection({});
        }
        setErrors((prev) => filterErrorsByKeys(prev, selectedIds));
      } else {
        setErrorMessage(response.data?.duesBulkEditRates?.message || "Failed to update dues rates");
      }
    });
  };

  const handleBulkAdjustRate = async (value: string) => {
    const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    updateDuesRates({
      variables: {
        input: {
          duesRateIds: selectedIds,
          percent: Number(value)
        }
      },
      refetchQueries: [
        {
          query: GET_DUES_RATES_QUERY,
          variables: {
            filters: {
              ...variables
            }
          }
        }
      ]
    }).then((response) => {
      if (response.data?.duesBulkEditRates?.success) {
        setOpenAdjustRateModal(false);
        showSnackbar("Rate(s) have been updated");
        trackEvent(
          SECTION_ANALYTICS_EVENTS.duesRatesUpdated,
          getSectionAnalyticsData({
            source: "section_dues_rates",
            sectionId,
            rateCount: selectedIds.length,
            action: "bulk_adjust",
            membershipYear: currentYear
          })
        );
        setErrors((prev) => filterErrorsByKeys(prev, selectedIds));
      } else {
        setErrorMessage(response.data?.duesBulkEditRates?.message || "Failed to update dues rates");
      }
    });
  };

  const handleEditRate = (value: string, cell: Cell<DuesRatesRow, unknown>) => {
    updateDuesRates({
      variables: {
        input: {
          duesRateIds: [cell.row.id],
          priceCents: Number(value.replace(/[$.]/g, ""))
        }
      },
      refetchQueries: [
        {
          query: GET_DUES_RATES_QUERY,
          variables: {
            filters: {
              ...variables
            }
          }
        }
      ]
    }).then((response) => {
      if (response.data?.duesBulkEditRates?.success) {
        showSnackbar("Rate(s) have been updated");
        trackEvent(
          SECTION_ANALYTICS_EVENTS.duesRatesUpdated,
          getSectionAnalyticsData({
            source: "section_dues_rates",
            sectionId,
            rateCount: 1,
            action: "inline_edit",
            membershipYear: currentYear
          })
        );
        setErrors((prev) => prev.filter((error) => !Object.keys(error).includes(cell.row.id)));
      } else {
        setErrorMessage(response.data?.duesBulkEditRates?.message || "Failed to update dues rates");
      }
    });
  };

  const handleFinalizePricing = async () => {
    finalizeDuesRates({
      variables: {
        input: {
          kind: `section-${legacyId}`
        }
      },
      refetchQueries: [
        {
          query: GET_DUES_RATES_QUERY,
          variables: {
            filters: {
              ...variables
            }
          }
        }
      ]
    }).then((response) => {
      if (response.data?.duesFinalizeRates?.success) {
        showSnackbar(`${currentYear} Section Dues finalized`);
        trackEvent(
          SECTION_ANALYTICS_EVENTS.duesRatesFinalized,
          getSectionAnalyticsData({
            source: "section_dues_rates",
            sectionId,
            action: "finalize_section",
            membershipYear: currentYear
          })
        );
      }
      if (response.data?.duesFinalizeRates?.errors) {
        setErrors(response.data?.duesFinalizeRates?.errors);
      }
    });
  };
  const isNationalFinalized = data?.nationalDuesRates?.nodes.every((item) => item.finalized) || false;

  const showSelectionColumn =
    !tableData.every((row) => row.finalized) && canEdit && canManageDuesRates && isNationalFinalized;
  const columns = useMemo(() => {
    const dataColumns = getColumns({
      tableData,
      rowSelected,
      handleEditRate,
      canManageDuesRates: canEdit && canManageDuesRates && isNationalFinalized,
      setOpenEditRateModal,
      setRowSelection,
      currentYear
    });
    return (
      showSelectionColumn ? [buildBulkSelectionColumn<DuesRatesRow>(), ...dataColumns] : dataColumns
    ) as typeof dataColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, rowSelected, showSelectionColumn]);

  const isFinalized = tableData.every((item) => item.finalized);
  const headingBody = !isNationalFinalized
    ? `We are preparing to set annual dues rates for the upcoming member year July 1, ${currentYear} - June 30,  ${
        currentYear + 1
      }. Rates have been defaulted to the current year's rates for each classification. You will be able to edit the section's rates once the National rates have been finalized.`
    : canManageDuesRates && canFinalize && canEdit && !isFinalized
      ? `Please set the section's annual dues rates below for upcoming member year July 1, ${currentYear} - June 30, ${
          currentYear + 1
        }. Rates have been defaulted to the current year's rates for each classification. The deadline to Finalize Rates is Mar 21, ${currentYear}. Invoices will be sent to members/associates/affiliates on or after May 1, ${currentYear}.`
      : isNationalFinalized && canManageDuesRates && canFinalize && canEdit && isFinalized
        ? `Section annual dues for July 1, ${currentYear} - Jun 30, ${currentYear + 1} have been finalized.`
        : `You are not authorized to edit dues for this section.`;
  const headingActions = [
    ...(canManageDuesRates && canFinalize && !isFinalized && isNationalFinalized
      ? [{ label: "Finalize Dues", icon: "lock" as const, onClick: () => setOpenFinalizePricingModal(true) }]
      : [])
  ];

  return (
    <>
      <TableHeading body={headingBody} actions={headingActions} />
      <Table
        data={isSelectedOnly ? filteredData : tableData}
        loading={loading}
        onSortingChange={handleSort}
        sortingState={orderBy}
        rowSelection={rowSelection}
        enableRowSelection={(row) => !row.original.finalized && isNationalFinalized}
        onRowSelectionChange={setRowSelection}
        columns={columns}
        renderControls={(table) => (
          <TableControls
            activeFilters={isActive ? 1 : 0}
            onClearAllFilters={() => {
              setVariables((prevValue) => ({ ...defaultVariables, search: prevValue.search }));
              setSelectedClassificationValues([]);
            }}
            bulkActions={[
              { label: "Edit Rate", icon: "edit", variant: "outlined", onClick: () => setOpenEditRateModal(true) },
              {
                label: "Adjust Rate by %",
                icon: "percent",
                variant: "outlined",
                onClick: () => setOpenAdjustRateModal(true)
              }
            ]}
            {...bulkSelectionProps}
            onDeselectAll={deselectAll}
            totalRowCount={tableData.length}
            onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
            allRowsSelected={table.getIsAllRowsSelected()}
            someRowsSelected={table.getIsSomeRowsSelected()}
          >
            <div className="w-full sm:max-w-[320px]">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <SearchInput placeholder="Search Product" query={field.value} onChange={field.onChange} />
                )}
              />
            </div>
          </TableControls>
        )}
        renderFooter={() => <TableFooter totalRowCount={tableData.length} {...bulkSelectionProps} />}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="request_quote" />
        )}
      />
      {openEditRateModal && (
        <EditRateModal
          onDismiss={() => {
            setOpenEditRateModal(false);
            if (isMobile && rowSelected === 1) {
              setRowSelection({});
            }
          }}
          handleBulkEditRate={handleBulkEditRate}
        />
      )}
      {openAdjustRateModal && (
        <AdjustRateModal
          onDismiss={() => {
            setOpenAdjustRateModal(false);
          }}
          handleBulkAdjustRate={handleBulkAdjustRate}
        />
      )}
      {openFinalizePricingModal && (
        <FinalizePricingModal
          errors={errors}
          onDismiss={() => {
            setOpenFinalizePricingModal(false);
          }}
          handleFinalizePricing={handleFinalizePricing}
          headline={`Finalize ${currentYear} Dues`}
          buttonText="Finalize Dues"
          description={`Are you sure you wish to finalize section dues for Jul 1, ${currentYear} - Jun 30, ${
            currentYear + 1
          }? The system will validate rates to ensure they follow Constitutional regulations, and you must correct any errors found. If there are no errors, PGA National will be notified that dues have been finalized and you will no longer be able to edit rates. If you need to submit rates that do not conform to the rules in the Constitution, please reach out to the National Membership Services team.`}
          successDescription={`Thank you for submitting dues rates for the upcoming membership year. Please contact National Membership
            Services if you have any questions.`}
          loading={finalizeLoading}
          success={tableData.every((item) => item.finalized)}
        />
      )}
    </>
  );
}
